import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { PermissionGuard } from '@/features/auth';
import { useServiceQuery } from '../hooks/useServiceQuery';
import { useServiceMutations } from '../hooks/useServiceMutations';
import { ServiceForm } from '../components/ServiceForm/ServiceForm';
import type { ServiceFormSubmitValues } from '../schemas/service.schema';
import type { BackRef } from '../types/backRef';
import { Alert, TextLink } from '@/shared/components';

export function ServiceEdit() {
    const { id } = useParams();
    const navigate = useNavigate();
    const { state } = useLocation();
    const backRef = (state as { backRef?: BackRef } | null)?.backRef;
    const query = useServiceQuery(id);
    const { updateMutation } = useServiceMutations();

    if (!id) return <p className="text-mp-error">Invalid service ID.</p>;
    if (query.isLoading) return <p>Loading service...</p>;
    if (query.isError || !query.data) {
        return <Alert variant="error"><span>Service not found or failed to load.</span></Alert>;
    }

    const service = query.data;

    const fallbackBackRef: BackRef = service.plan
        ? { path: `/smr-admin/plans/${service.plan.id}?tab=services`, label: service.plan.name }
        : { path: '/smr-admin/products', label: 'Products' };

    const effectiveBackRef = backRef ?? fallbackBackRef;

    const submit = (data: ServiceFormSubmitValues) =>
        updateMutation.mutateAsync({ id, payload: data })
            .then(() => navigate(`/smr-admin/services/${id}`, { state: { backRef: effectiveBackRef } }));

    return (
        <section className="space-y-4">
            <div className="flex items-center gap-2">
                <TextLink to={`/smr-admin/services/${id}`} state={{ backRef: effectiveBackRef }} color="secondary" className="text-sm">
                    &larr; Back to {service.name}
                </TextLink>
            </div>
            <h1 className="text-2xl font-semibold">Edit Service</h1>
            <p className="text-sm text-mp-muted">Plan: {service.plan?.name ?? '—'}</p>
            <PermissionGuard permission={{ resource: 'smr', action: 'service.edit' }}>
                <ServiceForm
                    initialValues={service}
                    onSubmit={submit}
                    isLoading={updateMutation.isPending}
                    submitLabel="Save Changes"
                />
            </PermissionGuard>
        </section>
    );
}
